import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notificationsService } from "../services/notificationsService";
import { groupNotificationsByDateAndType } from "../utils/dateUtils";

type UseNotificationsOptions = {
  mode?: "unread" | "all";
  refetchInterval?: number | false;
  enabled?: boolean;
  [key: string]: unknown;
};

export const useNotifications = (
  trialId: string,
  {
    mode = "unread",
    refetchInterval = 5 * 60 * 1000,
    enabled = true,
    ...queryOptions
  }: UseNotificationsOptions = {},
) => {
  const queryClient = useQueryClient();

  const unreadQuery = useQuery({
    queryKey: ["notifications", trialId, "unread"],
    queryFn: () =>
      notificationsService.fetchNotifications(trialId, {
        unread_only: "true",
      }),
    refetchInterval: mode === "unread" ? refetchInterval : false,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: enabled && !!trialId && mode === "unread",
    ...queryOptions,
  });

  const allQuery = useQuery({
    queryKey: ["notifications", trialId, "all"],
    queryFn: () => notificationsService.fetchNotifications(trialId),
    refetchInterval: mode === "all" ? refetchInterval : false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: enabled && !!trialId && mode === "all",
    ...queryOptions,
  });

  const markReadMutation = useMutation({
    mutationFn: (notificationId: string | number) =>
      notificationsService.markAsRead(trialId, notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", trialId] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsService.markAllAsRead(trialId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", trialId] });
    },
  });

  type NotificationItem = {
    notification_data?: { at?: string | number | Date };
    type: string;
  };
  const unreadNotifications = (unreadQuery.data as NotificationItem[]) || [];
  const allNotifications = (allQuery.data as NotificationItem[]) || [];
  const groupedNotifications =
    groupNotificationsByDateAndType(allNotifications);
  const allRead = unreadNotifications.length === 0;
  const unreadRefetch = unreadQuery.refetch;
  const allRefetch = allQuery.refetch;

  const refetch = useCallback(() => {
    if (mode === "unread") unreadRefetch();
    if (mode === "all") allRefetch();
  }, [allRefetch, mode, unreadRefetch]);

  const markAsRead = (notificationId: string | number) => {
    markReadMutation.mutate(notificationId);
  };

  const markAllAsRead = () => {
    markAllReadMutation.mutate();
  };

  return {
    unreadNotifications,
    allNotifications,
    unreadCount: unreadNotifications.length,
    groupedNotifications,
    allRead,
    isLoading: unreadQuery.isLoading || allQuery.isLoading,
    error: unreadQuery.error || allQuery.error,

    markReadMutation,
    markAllReadMutation,

    markReadError: markReadMutation.error,
    markAllReadError: markAllReadMutation.error,
    markAsReadLoading: markReadMutation.isPending,
    markAllAsReadLoading: markAllReadMutation.isPending,

    markAsRead,
    markAllAsRead,
    refetch,

    unreadQuery,
    allQuery,
  };
};
